// Сквозные отчёты. Только чтение.
//
// Пять вкладок — пять вопросов, на которые не отвечает ни один другой экран, потому что
// каждый ответ живёт МЕЖДУ двумя источниками. Правило показа у раздела одно и оно жёсткое:
// под каждой таблицей написано, ЗА КАКОЙ ПЕРИОД она вправе судить и чего не видит. Цифра
// без этой подписи в разделе про деньги — обвинение без даты.
//
// Расчётов здесь нет: всё считает ядро (xreports_api.py), правила показа — в lib/xreports.ts.

import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import {
  useXRSprav, useБанкПротивРеестра, useДеньгиБезЗакрытия, useЗакупкаБезПрихода,
  useПоставщикБезЗакрывающих, useРаботыБезСчёта,
} from '../api/xreports'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText } from '../lib/format'
import type {
  Граница, Окно, Паспорт, СтрокаБанк, СтрокаДеньги, СтрокаЗакупка, СтрокаПоставщик,
  СтрокаРаботы,
} from '../lib/xreports'
import {
  ПОДПИСЬ_МАТЧА, ПОДПИСЬ_СОСТОЯНИЯ, дни, окноЕсть, подписьГраницы, подписьОкна,
  почемуНетКандидата, рубли, суммаБезВалюты,
} from '../lib/xreports'

type Код = 'bank-invoice' | 'supplier-open' | 'supply-late' | 'works-unbilled' | 'money-unclosed'

const ВКЛАДКИ: [Код, string][] = [
  ['bank-invoice', 'Приход без разноски'],
  ['supplier-open', 'Заплатили, документов нет'],
  ['supply-late', 'Заказ есть, товара нет'],
  ['works-unbilled', 'Работа принята, счёта нет'],
  ['money-unclosed', 'Деньги есть, закрыть нечем'],
]

const ЕСТЬ_ВКЛАДКА = new Set<string>(ВКЛАДКИ.map(([к]) => к))
const СТРАНИЦА = 50

// ────────────────────────── общие куски разметки ──────────────────────────

/**
 * Границы источников и окно отчёта. Показывается ВСЕГДА и над таблицей, а не мелким
 * шрифтом внизу: человек читает цифру первой, и к моменту, когда он до неё дошёл,
 * он уже должен знать, за какой период она посчитана.
 */
function Границы({ окно, границы }: { окно?: Окно; границы?: Record<string, Граница> }) {
  const список = окно?.sources.map((к) => границы?.[к]).filter(Boolean) as Граница[] | undefined
  return (
    <div className="notice" style={{ marginBottom: 12 }}>
      <b>{подписьОкна(окно)}.</b>{' '}
      {окноЕсть(окно)
        ? 'За пределами этого периода отчёт молчит намеренно: там «записи нет» означает '
          + '«источник про те дни не знает», а не нарушение.'
        : 'Пустая таблица здесь означала бы «всё в порядке», и показывать её нельзя.'}
      {список && список.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {список.map((г) => (
            <li key={г.src} className="hint">{подписьГраницы(г)}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Итоги ВСЕГО отчёта — не страницы. Ядро считает их отдельным запросом. */
function Итоги({ подписи }: { подписи: [string, string][] }) {
  return (
    // Плитки-итоги общие для CRM (styles.css §7): раньше здесь стоял свой flex со своими
    // размерами (11px подпись, 18px число) — то же самое на глаз, но мимо токенов, и на
    // 320px плитки не переносились сеткой, а расползались строкой.
    <div className="tiles" style={{ marginBottom: 10 }}>
      {подписи.map(([что, значение]) => (
        <div className="tile" key={что}>
          <div className="t-label">{что}</div>
          <div className="t-sum">{значение}</div>
        </div>
      ))}
    </div>
  )
}

function Пейджер({ всего, start, next, занят, setStart }: {
  всего: number; start: number; next: number | null; занят: boolean
  setStart: (n: number) => void
}) {
  if (всего <= СТРАНИЦА) return null
  return (
    <div className="pager">
      <button disabled={start === 0 || занят} onClick={() => setStart(Math.max(0, start - СТРАНИЦА))}>
        ← назад
      </button>
      <span className="muted">{start + 1}–{Math.min(start + СТРАНИЦА, всего)} из {всего}</span>
      <button disabled={next === null || занят} onClick={() => next !== null && setStart(next)}>
        вперёд →
      </button>
    </div>
  )
}

/** Оговорки отчёта: чего он не видит. Ядро присылает их вместе с данными. */
function Оговорки({ заметки }: { заметки?: Record<string, string> }) {
  if (!заметки) return null
  return (
    <ul className="hint" style={{ marginTop: 10, paddingLeft: 18 }}>
      {Object.entries(заметки).map(([к, т]) => <li key={к}>{т}</li>)}
    </ul>
  )
}

const Пусто = ({ колонок, текст }: { колонок: number; текст: string }) => (
  <tr>
    <td colSpan={колонок} className="empty" data-label="">
      <div className="empty-title">{текст}</div>
    </td>
  </tr>
)

/** Ссылка на сделку. Сделка может быть не проставлена — тогда прочерк, а не пустота. */
const Сделка = ({ id, title }: { id: number | null; title: string | null }) =>
  id ? <Link to={`/deals/${id}`}>{fmtText(title) || `#${id}`}</Link> : <span className="muted">не указана</span>

// ────────────────────────────── экран ──────────────────────────────

export default function CrossReports() {
  const [параметры, setПараметры] = useSearchParams()
  const изАдреса = параметры.get('tab') || ''
  const вкладка: Код = (ЕСТЬ_ВКЛАДКА.has(изАдреса) ? изАдреса : 'bank-invoice') as Код

  const [поиск, setПоиск] = useState('')
  const [давно, setДавно] = useState(false)
  const [start, setStart] = useState(0)

  const refs = useXRSprav()
  const паспорт: Паспорт | undefined = refs.data?.reports.find((о) => о.code === вкладка)
  const порог = refs.data?.hang_days ?? 30

  // Адрес — часть состояния: ссылку на конкретный отчёт должно быть можно переслать.
  const переключить = (к: Код) => {
    setПараметры({ tab: к })
    setПоиск('')
    setДавно(false)
    setStart(0)
  }
  const сбросить = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  const общие = useMemo(
    () => ({ q: поиск.trim().length >= 2 ? поиск.trim() : undefined, start: start || undefined }),
    [поиск, start],
  )
  const сДавно = useMemo(() => ({ ...общие, aged: давно || undefined }), [общие, давно])

  const банк = useБанкПротивРеестра(общие, вкладка === 'bank-invoice')
  const поставщик = useПоставщикБезЗакрывающих(сДавно, вкладка === 'supplier-open')
  const закупка = useЗакупкаБезПрихода(сДавно, вкладка === 'supply-late')
  const работы = useРаботыБезСчёта(сДавно, вкладка === 'works-unbilled')
  const деньги = useДеньгиБезЗакрытия(сДавно, вкладка === 'money-unclosed')

  const текущий = { 'bank-invoice': банк, 'supplier-open': поставщик, 'supply-late': закупка,
    'works-unbilled': работы, 'money-unclosed': деньги }[вкладка]
  const данные = текущий.data

  return (
    <>
      <div className="page-head">
        <h1>Сквозные отчёты</h1>
        <span className="spacer" />
        <span className="hint">
          {refs.data?.oldest?.last_sync_at
            ? `самое старое зеркало обновлено ${fmtDate(refs.data.oldest.last_sync_at, true)}`
            : ''}
        </span>
      </div>

      <div className="notice" style={{ marginBottom: 12 }}>
        <b>Здесь показано то, что происходит между источниками.</b> Каждая цифра сведена
        из двух и более учётов, у которых разные границы, и потому у каждой таблицы своё
        окно. Расхождение состава сделки с суммой и дебиторка этот раздел не считает —
        они живут на своих экранах, и второй правды здесь нет.
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {ВКЛАДКИ.map(([код, подпись]) => (
          <button key={код} onClick={() => переключить(код)} aria-pressed={вкладка === код}
                  style={{ fontWeight: вкладка === код ? 600 : 400, flex: '0 1 auto' }}>
            {подпись}
          </button>
        ))}
      </div>

      {паспорт && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{паспорт.question}</div>
          <div className="hint">{паспорт.why}</div>
          <div className="hint" style={{ marginTop: 6 }}>
            <b>Чего отчёт не видит:</b> {паспорт.blind}
          </div>
        </div>
      )}

      <Границы окно={данные?.window ?? паспорт?.window} границы={данные?.bounds ?? refs.data?.bounds} />

      <FilterBar id={`xr-${вкладка}`} style={{ marginBottom: 12 }}>
        <div className="f" style={{ flex: 2, minWidth: 220 }}>
          <label htmlFor="xr-q">Поиск</label>
          <input id="xr-q" value={поиск} onChange={(e) => сбросить(setПоиск)(e.target.value)}
                 placeholder="от двух символов" />
        </div>
        {вкладка !== 'bank-invoice' && (
          <div className="f">
            <label htmlFor="xr-aged">Возраст</label>
            <select id="xr-aged" value={давно ? '1' : ''}
                    onChange={(e) => сбросить(setДавно)(e.target.value === '1')}>
              <option value="">любой</option>
              <option value="1">висит дольше {порог} дн.</option>
            </select>
          </div>
        )}
      </FilterBar>

      <QueryState isLoading={текущий.isLoading} error={текущий.error}>
        <div className="card">
          {вкладка === 'bank-invoice' && <ТаблицаБанк д={банк.data} />}
          {вкладка === 'supplier-open' && <ТаблицаПоставщик д={поставщик.data} порог={порог} />}
          {вкладка === 'supply-late' && <ТаблицаЗакупка д={закупка.data} порог={порог} />}
          {вкладка === 'works-unbilled' && <ТаблицаРаботы д={работы.data} порог={порог} />}
          {вкладка === 'money-unclosed' && <ТаблицаДеньги д={деньги.data} порог={порог} />}

          <Пейджер всего={данные?.total ?? 0} start={start} next={данные?.next ?? null}
                   занят={текущий.isFetching} setStart={setStart} />
          <Оговорки заметки={данные?.data_notes} />
        </div>
      </QueryState>
    </>
  )
}

// ────────────────────────────── таблицы ──────────────────────────────

type Д<T> = { rows: T[]; total: number; totals: Record<string, number | string | null> } | undefined

function ТаблицаБанк({ д }: { д: Д<СтрокаБанк> }) {
  const т = д?.totals ?? {}
  return (
    <>
      <Итоги подписи={[
        ['не разнесено операций', String(т.n ?? '—')],
        ['на сумму', рубли(т.rub as number)],
        ['кандидат один', String(т.n_one ?? '—')],
        ['опознаётся на сумму', рубли(т.rub_one as number)],
        ['искали, не нашли', String(т.n_looked ?? '—')],
        ['искать было не по чему', String(т.n_no_ref ?? '—')],
      ]} />
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th className="nowrap">Дата</th>
              <th className="num">Сумма</th>
              <th>Плательщик</th>
              <th>Назначение</th>
              <th>Кандидат из реестра</th>
            </tr>
          </thead>
          <tbody>
            {(д?.rows ?? []).map((с) => (
              <tr key={с.src_id}>
                <td className="nowrap" data-label="Дата">{fmtDate(с.op_date)}</td>
                <td className="num" data-label="Сумма">{рубли(с.amount)}</td>
                <td data-label="Плательщик">
                  {fmtText(с.cp_name)}
                  <div className="hint">ИНН {fmtText(с.cp_inn)}</div>
                </td>
                {/* Назначение платежа — длинный текст рядом с суммой, поэтому оно мельче.
                    Мельче — классом `.hint`, а не `fontSize: 12` руками: тот же размер, но
                    из токена, как в остальных разделах. */}
                <td data-label="Назначение" className="hint">{fmtText(с.purpose)}</td>
                <td data-label="Кандидат из реестра">
                  {с.match === 'one' && с.inv_id ? (
                    <>
                      <Сделка id={с.deal_id} title={с.deal_title} />
                      <div className="hint">
                        счёт №{fmtText(с.refs)} на {рубли(с.inv_sum)} — {ПОДПИСЬ_МАТЧА.one}
                      </div>
                    </>
                  ) : с.match === 'many' ? (
                    <span className="muted">
                      {ПОДПИСЬ_МАТЧА.many} ({с.n_cand}) — выбрать за человека нельзя
                    </span>
                  ) : (
                    <span className="muted">{почемуНетКандидата(с)}</span>
                  )}
                </td>
              </tr>
            ))}
            {д && д.rows.length === 0 && (
              <Пусто колонок={5} текст="Под эти фильтры неразнесённых приходов нет" />
            )}
          </tbody>
        </table>
      </div>
      <div className="hint" style={{ marginTop: 8 }}>
        Кандидат — подсказка, а не решение: платёж разносит бухгалтер. Совпасть должны
        номер счёта, сумма копейка в копейку и порядок во времени.
      </div>
    </>
  )
}

function ТаблицаПоставщик({ д, порог }: { д: Д<СтрокаПоставщик>; порог: number }) {
  const т = д?.totals ?? {}
  return (
    <>
      <Итоги подписи={[
        ['заявок', String(т.n ?? '—')],
        ['оплачено, рубли', рубли(т.rub as number)],
        // Число и сумма рядом — про РАЗНЫЕ множества, и это надо сказать, а не оставить
        // угадывать: `n_aged` с 28.07.2026 считает все валюты (иначе плитка расходилась
        // со списком: 424 против 426), а сумма рублёвая — складывать юани с рублями нельзя.
        // Поэтому третьей строкой стоит рублёвая часть счёта.
        [`висит дольше ${порог} дн.`, String(т.n_aged ?? '—')],
        ['из них рублёвых', String(т.n_aged_rub ?? '—')],
        ['на сумму, рубли', рубли(т.rub_aged as number)],
        ['в другой валюте', String(т.n_other_currency ?? '—')],
        ['самая ранняя оплата', т.first_paid ? fmtDate(String(т.first_paid)) : '—'],
      ]} />
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th className="nowrap">Оплачен</th>
              <th className="num">Висит</th>
              <th className="num">Сумма</th>
              <th>Поставщик</th>
              <th>За что</th>
              <th>Сделка</th>
            </tr>
          </thead>
          <tbody>
            {(д?.rows ?? []).map((с) => (
              <tr key={с.bitrix_id}>
                <td className="nowrap" data-label="Оплачен">{fmtDate(с.paid_on)}</td>
                <td className="num" data-label="Висит"
                    style={{ fontWeight: (с.days ?? 0) > порог ? 600 : 400 }}>{дни(с.days)}</td>
                <td className="num" data-label="Сумма">
                  {с.currency === 'RUB' ? рубли(с.opportunity)
                    : `${суммаБезВалюты(с.opportunity)} ${fmtText(с.currency)}`}
                </td>
                <td data-label="Поставщик">
                  {fmtText(с.supplier)}
                  <div className="hint">счёт №{fmtText(с.supplier_doc_no)}</div>
                </td>
                <td data-label="За что" className="hint">{fmtText(с.what_for)}</td>
                <td data-label="Сделка"><Сделка id={с.deal_id} title={с.deal_title} /></td>
              </tr>
            ))}
            {д && д.rows.length === 0 && (
              <Пусто колонок={6} текст="Под эти фильтры незакрытых оплат поставщикам нет" />
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

function ТаблицаЗакупка({ д, порог }: {
  д: (Д<СтрокаЗакупка> & {
    by_currency?: { currency_ref: string; n: number; amount: number }[]
    main_currency_ref?: string | null
  }) | undefined
  порог: number
}) {
  const т = д?.totals ?? {}
  const основная = д?.by_currency?.find((в) => в.currency_ref === д?.main_currency_ref)
  return (
    <>
      <Итоги подписи={[
        ['заказов без прихода', String(т.n ?? '—')],
        [`старше ${порог} дн.`, String(т.n_aged ?? '—')],
        ['в основной валюте', основная ? суммаБезВалюты(основная.amount) : '—'],
        ['валют в выборке', String(т.n_currencies ?? '—')],
        ['со сделкой Bitrix', String(т.n_with_deal ?? '—')],
      ]} />
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th className="nowrap">Заказ</th>
              <th className="num">Ждём</th>
              <th className="num">Сумма</th>
              <th>Счёт поставщика</th>
              <th>Сделка</th>
              <th>Статус в 1С</th>
            </tr>
          </thead>
          <tbody>
            {(д?.rows ?? []).map((с) => (
              <tr key={с.ref_key}>
                <td className="nowrap" data-label="Заказ">
                  {fmtText(с.number)}
                  <div className="hint">от {fmtDate(с.doc_date)}</div>
                </td>
                <td className="num" data-label="Ждём"
                    style={{ fontWeight: (с.days ?? 0) > порог ? 600 : 400 }}>{дни(с.days)}</td>
                <td className="num" data-label="Сумма">{суммаБезВалюты(с.sum_doc)}</td>
                <td data-label="Счёт поставщика">{fmtText(с.supplier_doc_no)}</td>
                <td data-label="Сделка"><Сделка id={с.deal_id} title={с.deal_title} /></td>
                <td data-label="Статус в 1С" className="muted">{fmtText(с.status)}</td>
              </tr>
            ))}
            {д && д.rows.length === 0 && (
              <Пусто колонок={6} текст="Под эти фильтры заказов без прихода нет" />
            )}
          </tbody>
        </table>
      </div>
      <div className="hint" style={{ marginTop: 8 }}>
        Суммы без знака валюты: валюта документа приезжает из 1С ссылкой, а справочника
        валют в ядре нет. Складывать их вместе нельзя, поэтому итог показан только
        по основной валюте.
      </div>
    </>
  )
}

function ТаблицаРаботы({ д, порог }: { д: Д<СтрокаРаботы>; порог: number }) {
  const т = д?.totals ?? {}
  return (
    <>
      <Итоги подписи={[
        ['сделок с принятой работой', String(т.n ?? '—')],
        ['счёта нет', String(т.n_no_invoice ?? '—')],
        ['сумма таких сделок', рубли(т.rub_no_invoice as number)],
        ['счёт есть, не оплачен', String(т.n_unpaid ?? '—')],
        [`приняли больше ${порог} дн. назад`, String(т.n_aged ?? '—')],
      ]} />
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th className="nowrap">Приняли</th>
              <th className="num">Прошло</th>
              <th>Сделка</th>
              <th className="num">Сумма сделки</th>
              <th>Что сдано</th>
              <th>Деньги</th>
            </tr>
          </thead>
          <tbody>
            {(д?.rows ?? []).map((с) => (
              <tr key={с.deal_id}>
                <td className="nowrap" data-label="Приняли">{fmtDate(с.accepted_at)}</td>
                <td className="num" data-label="Прошло"
                    style={{ fontWeight: (с.days ?? 0) > порог ? 600 : 400 }}>{дни(с.days)}</td>
                <td data-label="Сделка"><Сделка id={с.deal_id} title={с.deal_title} /></td>
                <td className="num" data-label="Сумма сделки">{рубли(с.opportunity)}</td>
                <td data-label="Что сдано" className="hint">
                  {с.n_verif > 0 && `верификаций: ${с.n_verif}`}
                  {с.n_verif > 0 && с.n_report > 0 && ', '}
                  {с.n_report > 0 && `отчётов: ${с.n_report}`}
                  {с.n_avr === 0 && <div>АВР нет — у поставки его и не бывает</div>}
                </td>
                <td data-label="Деньги">{ПОДПИСЬ_СОСТОЯНИЯ[с.state]}</td>
              </tr>
            ))}
            {д && д.rows.length === 0 && (
              <Пусто колонок={6} текст="Под эти фильтры принятых работ без счёта нет" />
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

function ТаблицаДеньги({ д, порог }: { д: Д<СтрокаДеньги>; порог: number }) {
  const т = д?.totals ?? {}
  return (
    <>
      <Итоги подписи={[
        ['сделок', String(т.n ?? '—')],
        ['пришло денег', рубли(т.rub as number)],
        [`последний платёж старше ${порог} дн.`, String(т.n_aged ?? '—')],
        ['на сумму', рубли(т.rub_aged as number)],
        ['сделка уже закрыта', String(т.n_closed_deals ?? '—')],
      ]} />
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Сделка</th>
              <th className="num">Пришло</th>
              <th className="num">Платежей</th>
              <th className="nowrap">Последний</th>
              <th className="num">Прошло</th>
              <th className="num">Сумма сделки</th>
            </tr>
          </thead>
          <tbody>
            {(д?.rows ?? []).map((с) => (
              <tr key={с.deal_id}>
                <td data-label="Сделка">
                  <Сделка id={с.deal_id} title={с.deal_title} />
                  {с.closed && <div className="hint">сделка закрыта</div>}
                </td>
                <td className="num" data-label="Пришло">{рубли(с.got)}</td>
                <td className="num muted" data-label="Платежей">{с.n_ops}</td>
                <td className="nowrap" data-label="Последний">{fmtDate(с.last_pay)}</td>
                <td className="num" data-label="Прошло"
                    style={{ fontWeight: (с.days ?? 0) > порог ? 600 : 400 }}>{дни(с.days)}</td>
                <td className="num muted" data-label="Сумма сделки">{рубли(с.opportunity)}</td>
              </tr>
            ))}
            {д && д.rows.length === 0 && (
              <Пусто колонок={6} текст="Под эти фильтры незакрытых поступлений нет" />
            )}
          </tbody>
        </table>
      </div>
      <div className="hint" style={{ marginTop: 8 }}>
        «Пришло» — деньги по выписке расчётного счёта, а не отметка «оплачен» в карточке.
        В таблицу попадают только сделки, у которых нет ни акта, ни реализации в 1С:
        порознь каждое из двух ничего не доказывает.
      </div>
    </>
  )
}
